// src/pages/ReportDetailPage.jsx
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { fetchReportDetail } from "../api/sowApi";
import { SkeletonText } from "../shared/ui";
import "../styles/reports.css";

export default function ReportDetailPage() {
  const { id } = useParams();
  const [report, setReport] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");
        const data = await fetchReportDetail(id);
        setReport(data);
        if (data.issues_json) {
          try {
            setIssues(JSON.parse(data.issues_json));
          } catch {
            setIssues([]);
          }
        }
      } catch (err) {
        console.error(err);
        setError("Failed to load report details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading && !report) {
    return (
      <div className="reports-page">
        <section className="report-detail-card">
          <SkeletonText lines={1} className="mb-4" />
          <SkeletonText lines={3} className="mb-4" />
          <SkeletonText lines={2} className="mb-4" />
          <SkeletonText lines={3} />
        </section>
      </div>
    );
  }

  if (error && !report) {
    return <div className="reports-page">{error}</div>;
  }

  if (!report) {
    return <div className="reports-page">Report not found.</div>;
  }

  return (
    <div className="reports-page">
      <section className="report-detail-card">
        <header className="report-detail-header">
          <div>
            <h1>SOW Analysis Detail</h1>
            <p>
              Report ID #{report.id} · Stage: {report.stage}
            </p>
          </div>
          <div className="score-pill">
            Score: {Math.round(report.overall_score)}
          </div>
        </header>

        <div className="report-meta">
          <div>
            <span className="label">SOW File</span>
            <span className="value">{report.sow_filename}</span>
          </div>
          <div>
            <span className="label">Created</span>
            <span className="value">
              {new Date(report.created_at).toLocaleString()}
            </span>
          </div>
          <div>
            <span className="label">Status</span>
            <span className="value">{report.status}</span>
          </div>
        </div>

        <div className="report-summary-block">
          <h2>Summary</h2>
          <p>{report.summary}</p>
        </div>

        <div className="report-scores">
          <div>
            <span className="label">Risk Score</span>
            <span className="value">
              {report.risk_score != null ? Math.round(report.risk_score) : "-"}
            </span>
          </div>
          <div>
            <span className="label">Compliance Score</span>
            <span className="value">
              {report.compliance_score != null
                ? Math.round(report.compliance_score)
                : "-"}
            </span>
          </div>
          <div>
            <span className="label">Commercial Score</span>
            <span className="value">
              {report.commercial_score != null
                ? Math.round(report.commercial_score)
                : "-"}
            </span>
          </div>
        </div>

        <div className="report-issues-block">
          <h2>Key Issues</h2>
          {issues.length === 0 && (
            <p className="muted">No issues recorded in this report.</p>
          )}
          <ul className="issues-list">
            {issues.map((issue, idx) => (
              <li key={idx} className={`issue severity-${issue.severity}`}>
                <div className="issue-header">
                  <span className="issue-dimension">
                    {issue.dimension || "General"}
                  </span>
                  <span className="issue-severity">{issue.severity}</span>
                </div>
                <p>{issue.description}</p>
                {issue.rule_name && (
                  <div className="issue-rule">
                    Related Rule: <strong>{issue.rule_name}</strong>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
